// Shared fetch wrapper with request deduplication and cache-aware GETs.
// Read responses are reused while the user navigates. Any successful write
// invalidates every cached GET, so the next read sees the changed database.

const TOKEN_KEY = 'clixtrack_token'
const TZ_KEY = 'affisee_timezone'
const INVALIDATE_KEY = 'clixtrack_api_cache_invalidate'
const GET_CACHE = new Map()
const IN_FLIGHT = new Map()

const inBrowser = typeof window !== 'undefined'

export const getToken = () => sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY)
export const setToken = (t) => { invalidateApiCache(); localStorage.setItem(TOKEN_KEY, t) }
export const setSessionToken = (t) => { invalidateApiCache(); sessionStorage.setItem(TOKEN_KEY, t) }
export const clearToken = () => { invalidateApiCache(); sessionStorage.removeItem(TOKEN_KEY); localStorage.removeItem(TOKEN_KEY) }

function withTimezone(path) {
  if (!inBrowser) return path
  if (!path || !path.startsWith('/api/')) return path

  const tz = localStorage.getItem(TZ_KEY) || 'UTC'

  // Avoid duplicate tz if any page already sends it manually.
  if (/[?&]tz=/.test(path)) return path

  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}tz=${encodeURIComponent(tz)}`
}

function cacheKey(path, auth) {
  // The JWT is intentionally not stored in the key: a login/logout clears the
  // cache, and this keeps sensitive tokens out of memory diagnostics.
  return `${auth ? 'auth' : 'public'}:${path}`
}

export function invalidateApiCache() {
  GET_CACHE.clear()
  IN_FLIGHT.clear()
  if (inBrowser) {
    try { localStorage.setItem(INVALIDATE_KEY, String(Date.now())) } catch { /* private mode */ }
  }
}

if (inBrowser) {
  window.addEventListener('storage', (event) => {
    if (event.key === INVALIDATE_KEY) {
      GET_CACHE.clear()
      IN_FLIGHT.clear()
    }

    // If timezone changes in another tab, clear cached GET responses.
    if (event.key === TZ_KEY) {
      GET_CACHE.clear()
      IN_FLIGHT.clear()
    }
  })

  // Fired by src/context/timezone.jsx when user changes timezone.
  window.addEventListener('clixtrack:timezone-change', () => {
    GET_CACHE.clear()
    IN_FLIGHT.clear()
  })
}

export async function api(path, {
  method = 'GET',
  body,
  auth = true,
  timeout = 15000,
  // force-cache: serve the first successful GET until a write invalidates it.
  // reload: send a conditional request and accept a fast 304 response.
  // no-store: always fetch and never retain the response.
  cache = method === 'GET' ? 'force-cache' : 'no-store',
} = {}) {
  path = withTimezone(path)

  const isGet = method.toUpperCase() === 'GET'
  const key = isGet ? cacheKey(path, auth) : null
  const cached = key ? GET_CACHE.get(key) : null

  if (isGet && cache === 'force-cache' && cached) return cached.data
  if (isGet && cache !== 'no-store' && IN_FLIGHT.has(key)) return IN_FLIGHT.get(key)

  const request = (async () => {
    const headers = { 'Content-Type': 'application/json' }
    if (auth) {
      const t = getToken()
      if (t) headers.Authorization = `Bearer ${t}`
    }
    if (isGet && cache === 'reload' && cached?.etag) headers['If-None-Match'] = cached.etag

    const controller = new AbortController()
    const timer = timeout ? setTimeout(() => controller.abort(), timeout) : null
    let res
    try {
      res = await fetch(path, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      })
    } catch (err) {
      if (err?.name === 'AbortError') throw new Error('Request timeout — please try again')
      throw err
    } finally {
      if (timer) clearTimeout(timer)
    }

    if (res.status === 304 && cached) return cached.data

    const raw = await res.text()
    let data = null
    try { data = raw ? JSON.parse(raw) : null } catch { data = { raw } }

    if (!res.ok) {
      if (res.status === 401) clearToken()
      const msg = (data && (data.error || data.message)) || `Request failed (${res.status})`
      throw new Error(msg)
    }

    if (isGet && cache !== 'no-store') {
      GET_CACHE.set(key, { data, etag: res.headers.get('ETag') || null })
    } else if (!isGet) {
      // A successful POST/PUT/PATCH/DELETE may change any dependent list.
      invalidateApiCache()
    }
    return data
  })()

  if (isGet && cache !== 'no-store') IN_FLIGHT.set(key, request)
  try {
    return await request
  } finally {
    if (isGet && IN_FLIGHT.get(key) === request) IN_FLIGHT.delete(key)
  }
}
